/**
 * DeviceModalThemeEditor Component
 * 
 * Main theme editor with Zoho-style UI:
 * - Left sidebar: Device type navigation
 * - Right content: Template cards with previews
 * - Edit button navigates to TemplateEditScreen
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Palette, Check, Loader2, AlertTriangle } from 'lucide-react';
import { DeviceModalConfig, TemplateStyleId } from '../deviceModals/types';
import { DeviceModalPreviewCard } from './DeviceModalPreview';
import { TemplateEditScreen } from './TemplateEditScreen';
import {
  getAllDeviceTypes,
  getAvailableTemplates,
  getDefaultConfig,
  getAllDeviceModalThemes,
  getAllTemplateConfigs,
  saveDeviceModalTheme,
  saveTemplateConfig,
  applyTemplate,
  deleteTemplate,
  resetDeviceModalTheme,
  DEVICE_TYPE_DISPLAY_NAMES,
  TemplateInfo
} from '../../services/deviceModalThemeService';
import { useLanguage } from '../../contexts/LanguageContext';
import { logger } from '../../utils/logger';

export interface DeviceModalThemeEditorProps {
  /** Optional callback when a theme is saved */
  onThemeSaved?: () => void;
}

export const DeviceModalThemeEditor: React.FC<DeviceModalThemeEditorProps> = ({
  onThemeSaved
}) => {
  const { language } = useLanguage();
  
  // State
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('light');
  const [themes, setThemes] = useState<Record<string, DeviceModalConfig>>({}); // Applied themes
  const [templateConfigs, setTemplateConfigs] = useState<Record<string, Record<string, DeviceModalConfig>>>({}); // Per-template configs
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<{
    deviceType: string;
    templateId: string;
    config: DeviceModalConfig;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    templateId: string;
    templateName: string;
  } | null>(null);

  // Get all device types
  const deviceTypes = getAllDeviceTypes();

  // Get templates for selected device type
  const templates = getAvailableTemplates(selectedDeviceType);

  // Get config for a specific template (either saved or default)
  // Returns a DEEP CLONE to prevent accidental mutation
  const getConfigForTemplate = useCallback((deviceType: string, templateId: string): DeviceModalConfig => {
    // First check if we have a saved config for this specific template
    const savedConfig = templateConfigs[deviceType]?.[templateId];
    if (savedConfig) {
      // Return a deep clone to prevent mutation
      return {
        ...savedConfig,
        templateId: templateId as TemplateStyleId, // Ensure correct templateId
        traitStyles: savedConfig.traitStyles ? { ...savedConfig.traitStyles } : undefined,
        attributeStyles: savedConfig.attributeStyles ? { ...savedConfig.attributeStyles } : undefined,
        traitOrder: savedConfig.traitOrder ? [...savedConfig.traitOrder] : undefined,
        hiddenComponents: savedConfig.hiddenComponents ? [...savedConfig.hiddenComponents] : undefined
      };
    }
    // Otherwise return the template's default config (already a new object)
    return getDefaultConfig(deviceType, templateId);
  }, [templateConfigs]);

  // Get currently applied config (for backward compatibility)
  const getCurrentConfig = useCallback((deviceType: string): DeviceModalConfig => {
    return themes[deviceType] || getDefaultConfig(deviceType);
  }, [themes]);

  // Load themes from Firestore
  useEffect(() => {
    const loadThemes = async () => {
      setIsLoading(true);
      try {
        // Load applied themes
        const loadedThemes = await getAllDeviceModalThemes();
        setThemes(loadedThemes);
        
        // Load per-template configs for the selected device type
        const loadedTemplateConfigs = await getAllTemplateConfigs(selectedDeviceType);
        setTemplateConfigs(prev => ({
          ...prev,
          [selectedDeviceType]: loadedTemplateConfigs
        }));
      } catch (error) {
        logger.error('Failed to load themes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadThemes();
  }, [selectedDeviceType]);

  // Show toast notification
  const showNotification = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Handle Apply button click (just apply, don't change config)
  const handleApply = async (templateId: string, templateName?: string) => {
    setIsSaving(true);
    try {
      // Get the config for this template (saved or default)
      const templateConfig = getConfigForTemplate(selectedDeviceType, templateId);
      
      // Apply the template (this saves it as applied AND saves its config)
      await saveDeviceModalTheme(selectedDeviceType, {
        ...templateConfig,
        templateId: templateId as TemplateStyleId
      });
      
      // Update local state
      setThemes(prev => ({
        ...prev,
        [selectedDeviceType]: {
          ...templateConfig,
          templateId: templateId as TemplateStyleId
        }
      }));
      
      showNotification(`Template applied: ${templateName || templateId}`);
      onThemeSaved?.();
    } catch (error) {
      logger.error('Failed to apply template:', error);
      showNotification('Failed to apply template');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Edit button click
  const handleEdit = (template: TemplateInfo) => {
    // Get the config for this specific template (saved or default)
    const editConfig = getConfigForTemplate(selectedDeviceType, template.id);
    
    setEditingTemplate({
      deviceType: selectedDeviceType,
      templateId: template.id,
      config: {
        ...editConfig,
        templateId: template.id as TemplateStyleId
      }
    });
  };

  // Handle save from edit screen (Save & Apply - saves, applies, and closes editor)
  const handleSaveFromEdit = async (config: DeviceModalConfig) => {
    if (!editingTemplate) return;
    
    setIsSaving(true);
    try {
      // Save AND apply (changes which template is active)
      await saveDeviceModalTheme(editingTemplate.deviceType, config);
      
      // Update applied themes state
      setThemes(prev => ({
        ...prev,
        [editingTemplate.deviceType]: config
      }));
      
      // Update per-template configs state
      setTemplateConfigs(prev => ({
        ...prev,
        [editingTemplate.deviceType]: {
          ...(prev[editingTemplate.deviceType] || {}),
          [editingTemplate.templateId]: config
        }
      }));
      
      showNotification('Theme saved and applied');
      onThemeSaved?.();
      setEditingTemplate(null); // Close editor
    } catch (error) {
      logger.error('Failed to save theme:', error);
      showNotification('Failed to save theme');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save only (Save - saves template config WITHOUT applying)
  const handleSaveOnlyFromEdit = async (config: DeviceModalConfig) => {
    if (!editingTemplate) return;
    
    setIsSaving(true);
    try {
      // Save only this template's config, don't change which template is applied
      await saveTemplateConfig(
        editingTemplate.deviceType,
        editingTemplate.templateId,
        config
      );
      
      // Update per-template configs state
      setTemplateConfigs(prev => ({
        ...prev,
        [editingTemplate.deviceType]: {
          ...(prev[editingTemplate.deviceType] || {}),
          [editingTemplate.templateId]: config
        }
      }));
      
      // Update the editing template's config to reflect saved state
      setEditingTemplate(prev => prev ? { ...prev, config } : null);
      
      showNotification('Template saved');
      onThemeSaved?.();
      // Don't close editor - stay on edit screen
    } catch (error) {
      logger.error('Failed to save template:', error);
      showNotification('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Save As from edit screen (save with new name)
  const handleSaveAsFromEdit = async (config: DeviceModalConfig, newName: string) => {
    if (!editingTemplate) return;
    
    setIsSaving(true);
    try {
      // Save with the new name
      const newConfig: DeviceModalConfig = {
        ...config,
        templateName: newName
      };
      
      await saveDeviceModalTheme(editingTemplate.deviceType, newConfig);
      
      setThemes(prev => ({
        ...prev,
        [editingTemplate.deviceType]: newConfig
      }));
      
      showNotification(`Saved as: ${newName}`);
      onThemeSaved?.();
      setEditingTemplate(null);
    } catch (error) {
      logger.error('Failed to save as new theme:', error);
      showNotification('Failed to save theme');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle back from edit screen
  const handleBackFromEdit = () => {
    setEditingTemplate(null);
  };

  // Handle Delete button click - show confirmation
  const handleDeleteClick = (templateId: string, templateName: string) => {
    setDeleteConfirmation({ templateId, templateName });
  };

  // Handle Delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;
    
    // Delete from registry
    const success = deleteTemplate(selectedDeviceType, deleteConfirmation.templateId);
    
    if (success) {
      showNotification(
        language === 'zh-TW' 
          ? `模板「${deleteConfirmation.templateName}」已刪除` 
          : `Template "${deleteConfirmation.templateName}" deleted`
      );
      
      // Force re-render by updating state
      // The templates list will refresh on next render
      setThemes(prev => ({ ...prev }));
    } else {
      showNotification(
        language === 'zh-TW' 
          ? '無法刪除模板' 
          : 'Cannot delete template'
      );
    }
    
    setDeleteConfirmation(null);
  };

  // Check if a template can be deleted
  const canDeleteTemplate = (templateId: string): boolean => {
    // Cannot delete if only one template
    if (templates.length <= 1) return false;
    
    // Cannot delete if currently applied
    const currentConfig = getCurrentConfig(selectedDeviceType);
    if (currentConfig.templateId === templateId) return false;
    
    return true;
  };

  // Get display name for device type
  const getDeviceTypeDisplayName = (type: string): string => {
    const names = DEVICE_TYPE_DISPLAY_NAMES[type];
    if (names) {
      return language === 'zh-TW' ? names.zh : names.en;
    }
    return type;
  };

  // If editing, show the edit screen
  if (editingTemplate) {
    return (
      <TemplateEditScreen
        deviceType={editingTemplate.deviceType}
        templateId={editingTemplate.templateId}
        initialConfig={editingTemplate.config}
        onSave={handleSaveFromEdit}
        onSaveOnly={handleSaveOnlyFromEdit}
        onSaveAs={handleSaveAsFromEdit}
        onBack={handleBackFromEdit}
        isSaving={isSaving}
      />
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className="flex items-center px-4 py-3 rounded-lg shadow-lg bg-green-500 text-white">
            <Check className="w-5 h-5 mr-2" />
            <span className="font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                {language === 'zh-TW' ? '確認刪除' : 'Confirm Delete'}
              </h3>
            </div>
            <p className="text-slate-300 mb-6">
              {language === 'zh-TW' 
                ? `確定要刪除模板「${deleteConfirmation.templateName}」嗎？此操作無法復原。`
                : `Are you sure you want to delete "${deleteConfirmation.templateName}"? This action cannot be undone.`
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                {language === 'zh-TW' ? '取消' : 'Cancel'}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                {language === 'zh-TW' ? '刪除' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left Sidebar - Device Type Navigation */}
      <aside className="w-56 bg-slate-800/30 border-r border-slate-700/50 overflow-y-auto flex-shrink-0">
        <div className="p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Device Types
          </h3>
          <nav className="space-y-1">
            {deviceTypes.map((type) => {
              const isSelected = type === selectedDeviceType;
              const appliedConfig = themes[type];
              const hasCustomTheme = !!appliedConfig;
              
              return (
                <button
                  key={type}
                  onClick={() => setSelectedDeviceType(type)}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between
                    ${isSelected 
                      ? 'bg-blue-600/20 text-white border border-blue-500/30' 
                      : 'text-slate-300 hover:bg-slate-700/50'
                    }
                  `}
                >
                  <span>{getDeviceTypeDisplayName(type)}</span>
                  {hasCustomTheme && (
                    <span className="w-2 h-2 rounded-full bg-green-500" title="Custom theme applied" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Right Content - Template Cards */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Palette className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">
              {getDeviceTypeDisplayName(selectedDeviceType)} Templates
            </h2>
          </div>
          <p className="text-sm text-slate-400">
            Select a template for {getDeviceTypeDisplayName(selectedDeviceType)} device modals. 
            Click "Edit" to customize styles.
          </p>
        </div>

        {/* Current Applied Template + Reset Button */}
        <div className="mb-4 flex items-center justify-between">
          {themes[selectedDeviceType] ? (
            <div className="px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <span className="text-sm text-green-400">
                <Check className="w-4 h-4 inline mr-1" />
                Currently Applied: {themes[selectedDeviceType].templateName || themes[selectedDeviceType].templateId}
              </span>
            </div>
          ) : (
            <div />
          )}
          <button
            onClick={async () => {
              if (window.confirm(language === 'zh-TW' 
                ? '確定要重置此設備類型的所有樣式設定嗎？這將清除所有已儲存的自訂樣式。'
                : 'Are you sure you want to reset all styles for this device type? This will clear all saved customizations.'
              )) {
                setIsSaving(true);
                try {
                  await resetDeviceModalTheme(selectedDeviceType);
                  // Clear local state
                  setThemes(prev => {
                    const newThemes = { ...prev };
                    delete newThemes[selectedDeviceType];
                    return newThemes;
                  });
                  setTemplateConfigs(prev => {
                    const newConfigs = { ...prev };
                    delete newConfigs[selectedDeviceType];
                    return newConfigs;
                  });
                  showNotification(language === 'zh-TW' ? '已重置為預設值' : 'Reset to defaults');
                } catch (error) {
                  logger.error('Failed to reset theme:', error);
                  showNotification(language === 'zh-TW' ? '重置失敗' : 'Reset failed');
                } finally {
                  setIsSaving(false);
                }
              }
            }}
            disabled={isSaving}
            className="px-3 py-1.5 text-xs text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
          >
            {language === 'zh-TW' ? '重置所有樣式' : 'Reset All Styles'}
          </button>
        </div>

        {/* Template Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {templates.map((template) => {
            const appliedConfig = themes[selectedDeviceType];
            const isApplied = appliedConfig?.templateId === template.id;
            
            // Get the config for this specific template (saved or default)
            const previewConfig = getConfigForTemplate(selectedDeviceType, template.id);
            
            // Use custom templateName if this template has one, otherwise use default name
            const savedTemplateConfig = templateConfigs[selectedDeviceType]?.[template.id];
            const displayName = savedTemplateConfig?.templateName || template.name;
            
            return (
              <DeviceModalPreviewCard
                key={template.id}
                templateId={template.id}
                templateName={displayName}
                deviceType={selectedDeviceType}
                isApplied={isApplied}
                canDelete={canDeleteTemplate(template.id)}
                config={{
                  ...previewConfig,
                  templateId: template.id as TemplateStyleId
                }}
                onEdit={() => handleEdit(template)}
                onApply={() => handleApply(template.id, displayName)}
                onDelete={() => handleDeleteClick(template.id, displayName)}
              />
            );
          })}
        </div>

        {/* Empty State */}
        {templates.length === 0 && (
          <div className="text-center py-12">
            <Palette className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No Templates Available</h3>
            <p className="text-sm text-slate-500">
              No templates have been created for this device type yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceModalThemeEditor;
